import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';
import { useProfileSummary } from '@/lib/hooks/useProfileSummary';
import { useAchievements } from '@/lib/hooks/useAchievements';
import { Avatar } from '@/features/Profile/Avatar';
import { CircularGauge } from '@/components/ui/circular-gauge';
import { LearningSnapshotCard } from '@/features/Profile/LearningSnapshotCard';
import { AchievementsCard } from '@/features/Profile/AchievementsCard';
import { AccountSettingsCard } from '@/features/Profile/AccountSettingsCard';

function formatEnrolledDate(iso: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const { data: summary } = useProfileSummary();
  const { data: achievements } = useAchievements();

  if (!user) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">Not logged in.</div>;
  }

  return (
    <div className="mx-auto w-full max-w-[1150px] space-y-8 px-4 py-6 sm:px-10 sm:py-10">
      {/* Page header */}
      <div className="min-w-0">
        <nav className="flex items-center gap-1.5 text-xs" aria-label="Breadcrumb">
          <Link to="/courses" className="text-[#9ca3af] transition-colors hover:text-[#6366f1]">
            My Batches
          </Link>
          {summary?.current_batch.name && (
            <>
              <ChevronRight className="size-3 text-[#9ca3af]" />
              <Link
                to="/courses/$batchId"
                params={{ batchId: summary.current_batch.id }}
                className="text-[#f97316] transition-opacity hover:opacity-80"
              >
                {summary.current_batch.name}
              </Link>
            </>
          )}
          <ChevronRight className="size-3 text-[#9ca3af]" />
          <span className="font-medium text-[#6366f1]">Profile</span>
        </nav>
        <div className="flex items-center gap-3 pt-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#ede9fe]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
            </svg>
          </span>
          <div>
            <h1 className="text-[34px] font-bold leading-[1.05] tracking-tight text-[#111827] sm:text-[40px]">Profile</h1>
          </div>
        </div>
        <p className="pt-2 text-sm text-[#9ca3af]">Manage your profile and view your learning summary.</p>
      </div>

      {/* Top card: identity + gauges */}
      <div className="grid grid-cols-1 gap-6 rounded-xl border border-[#e8e4df] bg-white p-6 sm:grid-cols-[1fr_auto]">
        <div className="flex items-center gap-4">
          <Avatar src={user.avatarUrl} name={user.username} size={72} />
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-bold text-[#1a1714]">{user.username}</h2>
            <p className="text-sm text-[#9a9589]">{user.email}</p>
            {summary?.current_batch.name && (
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-[#9a9589]">
                <span>Current Batch</span>
                <span className="rounded-full bg-[#ede9fe] px-2.5 py-0.5 font-medium text-[#7c3aed]">
                  {summary.current_batch.name}
                </span>
              </div>
            )}
            {summary?.enrolled_at && (
              <p className="mt-1 text-xs text-[#9a9589]">Enrolled on {formatEnrolledDate(summary.enrolled_at)}</p>
            )}
          </div>
        </div>
        {summary && (
          <div className="flex items-center justify-around gap-6 sm:justify-end">
            <CircularGauge value={Math.round(summary.progress_pct)} label="Progress" color="#7c3aed" />
            <CircularGauge value={Math.round(summary.attendance.pct)} label="Attendance" color="#e8660d" />
            <CircularGauge value={summary.streak.current} label="Streak" unit="days" color="#22c55e" />
          </div>
        )}
      </div>

      {/* Three cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {summary && (
          <LearningSnapshotCard snapshot={summary.learning_snapshot} batchId={summary.current_batch.id} />
        )}
        <AchievementsCard achievements={achievements ?? []} />
        <AccountSettingsCard />
      </div>
    </div>
  );
}
